/**
 * @fileOverview Loads the inventory data needed on the pickup page, pulls this
 * from summarisedMovements and maps it to the format the pickup page wants.
 */
import React, { useEffect, useState, useCallback } from 'react'
import {
  getPickupInventoryData,
  getPickupMissionData,
} from './../../../../../Module/graphQL/index'
import { authorization } from './../../../../../Module/Auth/Auth'
import { calculateTimeOfDay } from './../../../../../Utils/Mission'
import {
  toLocaleNonAmericanDateString,
  dropTimeZoneFromDate,
} from './../../../../../shared/libraries/utils/date'
import {
  INVENTORY_MOVEMENT_IN,
  INVENTORY_MOVEMENT_OUT,
} from '../../../../../shared/constants'

const LoadPickupData = props => {
  const {
    cityId,
    clientIds,
    campaignIds,
    children,
    searchText,
    ...otherProps
  } = props

  if (!cityId) throw new Error("'cityId' not provided.")

  var [givenOutCodes, setGivenOutCodes] = useState(null)
  var [availableCodes, setAvailableCodes] = useState(null)
  var [availableMerchandise, setAvailableMerchandise] = useState(null)
  var [givenOutMerchandise, setGivenOutMerchandise] = useState(null)
  //var [fetchStarted, setFetchStarted] = useState(false)
  var [isFetching, setIsFetching] = useState(true)
  var [isMissionsFetching, setIsMissionsFetching] = useState(true)
  var [missions, setMissions] = useState(null)

  useEffect(() => {
    setIsFetching(true)
    ;(async () => {
      var merchCursor = null,
        flyerCodesCursor = null

      let newGivenOutCodes = []
      let newAvailableCodes = []
      let newAvailableMerchandise = {}
      let newGivenOutMerchandise = {}

      do {
        const authToken = await authorization.getAPIKey()
        var response = await getPickupInventoryData(
          authToken,
          500,
          {
            merchCursor,
            flyerCodesCursor,
          },
          {
            cityId,
            current: true,
            active: true,
            includeLeftovers: false,
            clientIds,
            campaignIds,
            searchText,
          },
        )

        const result = await response.json()

        var {
          edges: flyerCodesEdges,
          pageInfo: flyerCodesPageInfo,
        } = result.data.flyerCodes
        var { edges: merchEdges, pageInfo: merchPageInfo } = result.data.merch
        const { edges: inventoryTypesEdges } = result.data.inventoryTypes

        if (result['errorMessage']) throw new Error(result['errorMessage'])
        if (result.errors && result.errors[0])
          throw new Error(result.errors[0].message)

        // A map of inventory type ids to the inventory name
        var inventoryTypesHash = {}
        inventoryTypesEdges.forEach(edge => {
          inventoryTypesHash[edge.node.id] = edge.node.name
        })

        var processInventoryItem = edge => {
          var movementsByType = {}
          var inventory = edge.node
          edge.node.summarisedMovements.nodes.forEach(movement => {
            if (movement.relatedCity.id != cityId) return
            movementsByType[movement.movementType.id] = movement
          })

          const TYPE_ID = inventory.inventoryType.id
          const IS_FLYER = inventory.inventoryType.id == '3'
          const IS_MERCH = inventory.inventoryType.id != '3'
          const IN_STOCK =
            movementsByType['1'] && movementsByType['1'].quantity > 0
          //const ON_BA = (movementsByType['2'] && movementsByType['2'].quantity > 0)
          const DISTRIBUTED =
            movementsByType['3'] && movementsByType['3'].quantity > 0

          // Figure out if it's given out or in stock

          edge.node.summarisedMovements.nodes.forEach(movement => {
            if (movement.relatedCity.id != cityId) return
            // Handle any assigned to user movements
            if (movement.movementType.id != '2') return

            if (!DISTRIBUTED) {
              if (IS_FLYER) {
                newGivenOutCodes.push({
                  uniqueKey: inventory.id,
                  inventoryId: inventory.id,
                  code: inventory.description,
                  ambassadorId: movement.relatedUser.id,
                  quantity: movement.quantity,
                  clientId: inventory.client.id,
                })
              }

              if (IS_MERCH) {
                newGivenOutMerchandise[
                  `${inventory.id}${movement.relatedUser.id}`
                ] = {
                  uniqueKey: inventory.id,
                  inventoryId: inventory.id,
                  title: inventoryTypesHash[TYPE_ID],
                  code: inventory.description,
                  ambassadorId: movement.relatedUser.id,
                  quantity: newGivenOutMerchandise[inventory.id]
                    ? movement.quantity +
                      newGivenOutMerchandise[inventory.id].quantity
                    : movement.quantity,
                  clientId: inventory.client.id,
                }
              }
            }
          })

          if (IN_STOCK) {
            if (IS_FLYER) {
              newAvailableCodes.push({
                uniqueKey: inventory.id,
                inventoryId: inventory.id,
                code: inventory.description,
                quantity: movementsByType['1'].quantity,
                clientId: inventory.client.id,
              })
            }
            if (IS_MERCH) {
              newAvailableMerchandise[
                `${inventory.id}${inventory.client.id}`
              ] = {
                uniqueKey: inventory.id,
                inventoryId: inventory.id,
                title: inventoryTypesHash[TYPE_ID],
                code: inventory.description,
                quantity: movementsByType['1'].quantity,
                clientId: inventory.client.id,
              }
            }
          }
        }

        merchEdges.forEach(processInventoryItem)
        flyerCodesEdges.forEach(processInventoryItem)

        flyerCodesCursor = flyerCodesPageInfo.endCursor
        merchCursor = merchPageInfo.endCursor
      } while (flyerCodesPageInfo.hasNextPage || merchPageInfo.hasNextPage)

      setGivenOutCodes(newGivenOutCodes)
      setAvailableCodes(newAvailableCodes)
      setGivenOutMerchandise(Object.values(newGivenOutMerchandise))
      setAvailableMerchandise(Object.values(newAvailableMerchandise))
      setIsFetching(false)
    })()
  }, [cityId, campaignIds, clientIds, searchText])

  useEffect(() => {
    //if (isMissionsFetching) return
    setIsMissionsFetching(true)
    ;(async () => {
      const authToken = await authorization.getAPIKey()
      var response = await getPickupMissionData(authToken, {
        cityId,
        now: new Date().toISOString(),
      })
      var result = await response.json()
      if (result['errorMessage']) throw new Error(result['errorMessage'])
      if (result.errors && result.errors[0])
        throw new Error(result.errors[0].message)

      let missions = []
      var mapMission = e => ({
        '#MISSION': e.node.id,
        'CAMPAIGN NAME': e.node.cityPlan.campaign.name,
        'DATE': toLocaleNonAmericanDateString(
          new Date(dropTimeZoneFromDate(e.node.dateTime)),
        ),
        'TIME': calculateTimeOfDay(
          e.node.dateTime,
          result.data.city.lunchCheckInTime,
          result.data.city.afternoonCheckInTime,
        ),
        'AMBASSADOR': e.node.user
          ? e.node.user.firstName + ' ' + e.node.user.lastName
          : '-',
        'SCHEDULED PICKUP TIME': e.node.pickupDateTime,
        'userId': e.node.user ? e.node.user.id : null,
        'clientId': e.node.cityPlan.campaign.client.id,
        'ambassadorMobilePhone': e.node.user ? e.node.user.mobilePhone : null,
        'ambassadorEmail': e.node.user ? e.node.user.email : null,
        'campaignId': e.node.cityPlan.campaign.id,
      })
      result.data.city.upcomingMissions.edges.forEach(m =>
        missions.push(mapMission(m)),
      )
      result.data.city.checkedInMissions.edges.forEach(m =>
        missions.push(mapMission(m)),
      )
      setMissions(missions)
      setIsMissionsFetching(false)
    })()
  }, [cityId])

  const updateLocalInventoryState = useCallback(
    ({ type, code, inventoryType }) => {
      if (inventoryType === 'Flyers') {
        if (type === INVENTORY_MOVEMENT_IN) {
          availableCodes.push(code)
          setAvailableCodes(availableCodes)
          setGivenOutCodes(givenOutCodes =>
            givenOutCodes.filter(
              givenOutCode => givenOutCode.code !== code.code,
            ),
          )
        }
        if (type === INVENTORY_MOVEMENT_OUT) {
          givenOutCodes.push(code)
          setGivenOutCodes(givenOutCodes)
          setAvailableCodes(availableCodes =>
            availableCodes.filter(
              availableCode => availableCode.code !== code.code,
            ),
          )
        }
      } else {
        if (type === INVENTORY_MOVEMENT_IN) {
          setGivenOutMerchandise(givenOutMerchandise =>
            givenOutMerchandise.reduce((acc, merchItem) => {
              if (
                code.uniqueKey === merchItem.uniqueKey &&
                code.previousAmbassadorId === merchItem.ambassadorId
              ) {
                const quantity = merchItem.quantity - 1
                if (quantity > 0) {
                  acc.push({
                    ...merchItem,
                    quantity,
                  })
                }
              } else {
                acc.push(merchItem)
              }
              return acc
            }, []),
          )

          setAvailableMerchandise(availableMerchandise => {
            const existingMerch = availableMerchandise.find(
              merchItem => code.uniqueKey === merchItem.uniqueKey,
            )
            if (!existingMerch) {
              availableMerchandise.push({ ...code, quantity: 0 })
            }
            return availableMerchandise.map(merchItem => {
              if (code.uniqueKey === merchItem.uniqueKey) {
                return {
                  ...code,
                  quantity: merchItem.quantity + 1,
                }
              }
              return merchItem
            })
          })
        }
        if (type === INVENTORY_MOVEMENT_OUT) {
          // if (code.previousAmbassadorId) {
          if (code.previousAmbassadorId) {
            setGivenOutMerchandise(givenOutMerchandise => {
              const existingMerch = givenOutMerchandise.find(
                merchItem =>
                  code.uniqueKey === merchItem.uniqueKey &&
                  code.ambassadorId === merchItem.ambassadorId,
              )
              if (!existingMerch) {
                givenOutMerchandise.push({ ...code, quantity: 0 })
              }

              return givenOutMerchandise.map(merchItem => {
                if (code.clientId === merchItem.clientId) {
                }
                if (
                  code.uniqueKey === merchItem.uniqueKey &&
                  code.ambassadorId === merchItem.ambassadorId
                ) {
                  return {
                    ...code,
                    quantity: merchItem.quantity + 1,
                  }
                }
                return merchItem
              })
            })

            setGivenOutMerchandise(givenOutMerchandise =>
              givenOutMerchandise.reduce((acc, merchItem) => {
                if (
                  code.uniqueKey === merchItem.uniqueKey &&
                  code.previousAmbassadorId === merchItem.ambassadorId
                ) {
                  const quantity = merchItem.quantity - 1
                  if (quantity > 0) {
                    acc.push({
                      ...merchItem,
                      quantity,
                    })
                  }
                } else {
                  acc.push(merchItem)
                }
                return acc
              }, []),
            )
          } else {
            setAvailableMerchandise(availableMerchandise =>
              availableMerchandise.reduce((acc, merchItem) => {
                if (code.uniqueKey === merchItem.uniqueKey) {
                  const quantity = merchItem.quantity - 1
                  if (quantity > 0) {
                    acc.push({
                      ...merchItem,
                      quantity,
                    })
                  }
                } else {
                  acc.push(merchItem)
                }
                return acc
              }, []),
            )

            setGivenOutMerchandise(givenOutMerchandise => {
              const existingMerch = givenOutMerchandise.find(
                merchItem =>
                  code.uniqueKey === merchItem.uniqueKey &&
                  code.ambassadorId === merchItem.ambassadorId,
              )
              if (!existingMerch) {
                givenOutMerchandise.push({ ...code, quantity: 0 })
              }

              return givenOutMerchandise.map(merchItem => {
                if (code.clientId === merchItem.clientId) {
                }
                if (
                  code.uniqueKey === merchItem.uniqueKey &&
                  code.ambassadorId === merchItem.ambassadorId
                ) {
                  return {
                    ...code,
                    quantity: merchItem.quantity + 1,
                  }
                }
                return merchItem
              })
            })
          }
        }
      }
    },
  )

  return React.cloneElement(children, {
    ...otherProps,
    cityId,
    updateLocalInventoryState,
    givenOutCodes,
    availableCodes,
    availableMerchandise,
    givenOutMerchandise,
    isFetching: isFetching || isMissionsFetching,
    missions,
  })
}

export default LoadPickupData



// WEBPACK FOOTER //
// ./src/Containers/Form/City/Pickup/new/PickupData.js